//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  IAnalyticsProviderSettings,
  IAnalyticsProvidersSettings,
} from '@account-settings/redux/analyticsProviders/analyticsProvidersModel';
import * as analyticsSettingsApi from '@apis/analytics-settings/analyticsSettingsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { recordEvent } from '@tracker/tracker';
import {
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
} from '@tracker/trackerModel';

const initialState: IAnalyticsProvidersSettings = {
  providerSettings: {
    adobeAnalyticsSettings: undefined,
    adobeAnalyticsOAuthSettings: undefined,
    pixelSettings: undefined,
  },
  modalStates: {
    isChangeGoogleAnalyticsProfileModalOpen: false,
    isGoogleAnalyticsFirstTimeSetupModalOpen: false,
    isConnectAdobeOAuthModalOpen: false,
  },
  errors: {
    fetchSettingsError: undefined,
    disconnectAdobeError: undefined,
    connectAdobeOAuthError: undefined,
    disconnectAdobeOAuthError: undefined,
  },
  flags: {
    isFetchingSettings: false,
    isDisconnectingAdobe: false,
    isConnectingAdobeOAuth: false,
    isDisconnectingAdobeOAuth: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsAnalyticsProviders,
  initialState,
  reducers: {
    requestAnalyticsProvidersSettings(state) {
      state.flags.isFetchingSettings = true;
      state.errors.fetchSettingsError = undefined;
    },
    receiveAnalyticsProvidersSettingsSucceeded(
      state,
      action: PayloadAction<IAnalyticsProviderSettings>,
    ) {
      state.flags.isFetchingSettings = false;
      state.providerSettings = {
        ...action.payload,
      };
    },
    receiveAnalyticsProvidersSettingsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isFetchingSettings = false;
      state.errors.fetchSettingsError = action.payload;
    },
    requestDisconnectAdobe(state) {
      state.flags.isDisconnectingAdobe = true;
      state.errors.disconnectAdobeError = undefined;
    },
    receiveDisconnectAdobeSucceeded(state) {
      state.flags.isDisconnectingAdobe = false;
    },
    receiveDisconnectAdobeFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnectingAdobe = false;
      state.errors.disconnectAdobeError = action.payload;
    },
    setIsChangeGoogleAnalyticsProfileModalOpen(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.modalStates.isChangeGoogleAnalyticsProfileModalOpen =
        action.payload;
    },
    setIsGoogleAnalyticsFirstTimeSetupModalOpen(
      state,
      action: PayloadAction<boolean>,
    ) {
      state.modalStates.isGoogleAnalyticsFirstTimeSetupModalOpen =
        action.payload;
    },
    setIsConnectAdobeOAuthModalOpen(state, action: PayloadAction<boolean>) {
      state.modalStates.isConnectAdobeOAuthModalOpen = action.payload;
    },
    requestConnectAdobeOAuth(state) {
      state.flags.isConnectingAdobeOAuth = true;
      state.errors.connectAdobeOAuthError = undefined;
    },
    receiveConnectAdobeOAuthSucceeded(state) {
      state.flags.isConnectingAdobeOAuth = false;
    },
    receiveConnectAdobeOAuthFailed(state, action: PayloadAction<string>) {
      state.flags.isConnectingAdobeOAuth = false;
      state.errors.connectAdobeOAuthError = action.payload;
    },
    requestDisconnectAdobeOAuth(state) {
      state.flags.isDisconnectingAdobeOAuth = true;
      state.errors.disconnectAdobeOAuthError = undefined;
    },
    receiveDisconnectAdobeOAuthSucceeded(state) {
      state.flags.isDisconnectingAdobeOAuth = false;
    },
    receiveDisconnectAdobeOAuthFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnectingAdobeOAuth = false;
      state.errors.disconnectAdobeOAuthError = action.payload;
    },
  },
});

export const getAnalyticsProviderSettings = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestAnalyticsProvidersSettings());
    try {
      const [
        adobeAnalyticsSettings,
        adobeAnalyticsOAuthSettings,
        pixelSettings,
      ] = await Promise.all([
        analyticsSettingsApi.getAdobeWebServiceSettings(),
        analyticsSettingsApi.getAdobeOAuthSettings(),
        analyticsSettingsApi.getCuralatePixelSettings(),
      ]);

      dispatch(
        actions.receiveAnalyticsProvidersSettingsSucceeded({
          adobeAnalyticsSettings,
          adobeAnalyticsOAuthSettings,
          pixelSettings,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveAnalyticsProvidersSettingsFailed(
          'An error occurred when trying to get your analytics provider settings.',
        ),
      );
    }
  },
);

export const disconnectAdobeWebService = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDisconnectAdobe());
  try {
    await analyticsSettingsApi.disconnectAdobeAnalyticsWebservice();

    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.SUCCESS,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Adobe Analytics account successfully disconnected!',
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        type: ToastType.SUCCESS,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeSucceeded());
    dispatch(getAnalyticsProviderSettings());
  } catch (_e) {
    const error =
      'An error occurred when trying to disconnect your Adobe Analytics account.';
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeFailed(error));
  }
});

// TODO: PD-326768 - Remove this legacy Redux flow when cleaning up the analytics-settings-ui-rework feature flag.
export const connectAdobeOAuth = createThunk(
  (
    reportSuiteId: string,
    adobeClientId: string,
    sharedSecret: string,
    scopes: string,
    adobeCompanyId?: string,
  ) =>
    async (dispatch) => {
      dispatch(actions.requestConnectAdobeOAuth());

      try {
        await analyticsSettingsApi.connectAdobeAnalyticsOAuth({
          reportSuiteId,
          adobeClientId,
          sharedSecret,
          scopes,
          // TODO: PD-319318: Remove adobeCompanyId fully once AnalyticsConfigurationMigration flag is removed
          ...(adobeCompanyId && { adobeCompanyId }),
        });

        recordEvent(
          TRACKING_CATEGORY.SETTINGS,
          TRACKING_ACTION.CONNECT_ACCOUNT,
          TRACKING_LABEL.SUCCESS,
        );

        dispatch(actions.receiveConnectAdobeOAuthSucceeded());
        void dispatch(getAnalyticsProviderSettings());
        dispatch(setIsConnectAdobeOAuthModalOpen(false));
      } catch (_e) {
        const error =
          'An error occurred when trying to connect your Adobe Analytics account.';
        recordEvent(
          TRACKING_CATEGORY.SETTINGS,
          TRACKING_ACTION.CONNECT_ACCOUNT,
          TRACKING_LABEL.FAIL,
        );

        dispatch(actions.receiveConnectAdobeOAuthFailed(error));
      }
    },
);

export const disconnectAdobeOAuth = createThunk(() => async (dispatch) => {
  dispatch(actions.requestDisconnectAdobeOAuth());
  try {
    await analyticsSettingsApi.disconnectAdobeAnalyticsOAuth();

    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.SUCCESS,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Adobe Analytics account successfully disconnected!',
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        type: ToastType.SUCCESS,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeOAuthSucceeded());
    dispatch(getAnalyticsProviderSettings());
  } catch (_e) {
    const error =
      'An error occurred when trying to disconnect your Adobe Analytics account.';
    recordEvent(
      TRACKING_CATEGORY.SETTINGS,
      TRACKING_ACTION.DISCONNECT_ACCOUNT,
      TRACKING_LABEL.FAIL,
    );

    dispatch(
      toastNotificationsActions.addToastNotification({
        title: error,
        options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        type: ToastType.ERROR,
      }),
    );
    dispatch(actions.receiveDisconnectAdobeOAuthFailed(error));
  }
});

export const {
  setIsChangeGoogleAnalyticsProfileModalOpen,
  setIsConnectAdobeOAuthModalOpen,
  // TODO: PD-326768 - Remove once the feature is released
  setIsGoogleAnalyticsFirstTimeSetupModalOpen,
} = actions;

export default reducer;
